import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../config';
import { startOfDay, endOfDay } from 'date-fns';
import { entrySource, MANUAL, type EntrySource, type HealthPlatform } from '../../health/healthImportModel';
import { recordImportTombstone } from './healthImport';

export interface Meal {
  id: string;
  userId: string;
  name: string;
  time: Date;
  notes?: string;
  calories?: number;
  createdAt: Date;
  updatedAt: Date;
  /**
   * Where this entry came from. A stored value that is absent — or anything
   * other than 'health' — classifies as manual, exactly as it does on mobile;
   * see entrySource.
   */
  source?: EntrySource;
  /** Imported entries only. */
  healthPlatform?: HealthPlatform;
  /** Imported entries only: the raw platform record id. */
  healthRecordId?: string;
}

export interface CreateMealParams {
  userId: string;
  name: string;
  time: Date;
  notes?: string;
  calories?: number;
}

export interface UpdateMealParams {
  name?: string;
  time?: Date;
  notes?: string;
  calories?: number;
}

// Get today's meals for a user
export async function getTodayMeals(userId: string): Promise<Meal[]> {
  try {
    const today = new Date();
    const start = startOfDay(today);
    const end = endOfDay(today);
    
    const mealsQuery = query(
      collection(db, 'meals'),
      where('userId', '==', userId),
      where('time', '>=', Timestamp.fromDate(start)),
      where('time', '<=', Timestamp.fromDate(end)),
      orderBy('time', 'desc')
    );
    
    const snapshot = await getDocs(mealsQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        time: data.time?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        // AFTER the spread: the raw stored value must never reach the UI.
        source: entrySource(data.source),
      } as Meal;
    });
  } catch (error) {
    console.error('Error fetching meals:', error);
    throw error;
  }
}

// Get meals for a specific date range
export async function getMealsByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Meal[]> {
  try {
    const start = startOfDay(startDate);
    const end = endOfDay(endDate);
    
    const mealsQuery = query(
      collection(db, 'meals'),
      where('userId', '==', userId),
      where('time', '>=', Timestamp.fromDate(start)),
      where('time', '<=', Timestamp.fromDate(end)),
      orderBy('time', 'desc')
    );
    
    const snapshot = await getDocs(mealsQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        time: data.time?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        // AFTER the spread: the raw stored value must never reach the UI.
        source: entrySource(data.source),
      } as Meal;
    });
  } catch (error) {
    console.error('Error fetching meals by date range:', error);
    throw error;
  }
}

// Create a meal
export async function createMeal(params: CreateMealParams): Promise<string> {
  try {
    const mealData = {
      userId: params.userId,
      name: params.name,
      time: Timestamp.fromDate(params.time),
      notes: params.notes || '',
      calories: params.calories || null,
      source: MANUAL,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'meals'), mealData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating meal:', error);
    throw error;
  }
}

// Update a meal
export async function updateMeal(mealId: string, params: UpdateMealParams): Promise<void> {
  try {
    const updateData: any = {
      updatedAt: Timestamp.now()
    };
    
    if (params.name !== undefined) updateData.name = params.name;
    if (params.time !== undefined) updateData.time = Timestamp.fromDate(params.time);
    if (params.notes !== undefined) updateData.notes = params.notes;
    if (params.calories !== undefined) updateData.calories = params.calories;
    
    await updateDoc(doc(db, 'meals', mealId), updateData);
  } catch (error) {
    console.error('Error updating meal:', error);
    throw error;
  }
}

// Delete a meal
export async function deleteMeal(mealId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'meals', mealId));
    // Without this, the phone's next import pass recreates the entry the user
    // just deleted here.
    await recordImportTombstone(mealId);
  } catch (error) {
    console.error('Error deleting meal:', error);
    throw error;
  }
}

